/**
 * SISTEMA: PGLP | MONTE HERMOM
 * COMPONENTE: LaudoRevisaoNarrativa_soberana
 * LOCALIZAÇÃO: /src/components/laudo/
 * VERSÃO: 2.0.0 — Defensiva
 * DESCRIÇÃO: Câmara de refino intelectual para edição final do texto pericial.
 */

import React, { useState, useEffect } from 'react';
import {
    Edit3, Save, ShieldCheck, BookOpen,
    MessageSquareQuote, AlertTriangle, FileSearch
} from 'lucide-react';

export default function LaudoRevisaoNarrativa_soberana({
    dadosConsolidados = {},
    onConfirm
}) {
    // ESTADO SOBERANO DA NARRATIVA
    const [narrativa, setNarrativa] = useState({
        parecerFinal: "",
        justificativaTecnica: "",
        ressalvasNormativas: "Nada a ressalvar quanto à NBR 14653-2."
    });

    // INJEÇÃO DE CONTEXTO AUTOMÁTICA (Link com a Consolidação)
    useEffect(() => {
        const r = dadosConsolidados || {};

        const processo = r.requerente?.processoJudicial || 'Não Informado';
        const valor    = r.resultado?.valorTotal?.toLocaleString('pt-BR') || '0,00';
        const indice   = r.resultado?.indiceValorizacao || '0';
        const ativos   = r.ativosVistoria?.quantidade || 0;

        const textoInjetado =
            `Trata-se de avaliação imobiliária para fins judiciais (Processo: ${processo}). \n\n` +
            `Identificou-se o valor de mercado de R$ ${valor}, ` +
            `considerando o Fator de Localização Regional de ${indice}%. \n\n` +
            `A infraestrutura detectada na vistoria (Ativos: ${ativos}) justifica plenamente a homogeneização aplicada.`;

        setNarrativa(prev => ({ ...prev, parecerFinal: textoInjetado }));
    }, [dadosConsolidados]);

    const confirmarRevisao = () => {
        if (!narrativa.parecerFinal || narrativa.parecerFinal.length < 100) {
            alert("ERRO DE SOBERANIA: A fundamentação narrativa é insuficiente para o padrão Monte Hermom.");
            return;
        }
        if (typeof onConfirm === 'function') {
            onConfirm(narrativa);
        }
    };

    /* ---------- VALORES COM FALLBACK ---------- */
    const valorTotal        = dadosConsolidados?.resultado?.valorTotal;
    const valorFormatado    = valorTotal ? valorTotal.toLocaleString('pt-BR') : '—';

    return (
        <div className="w-full space-y-12 animate-in fade-in slide-in-from-right-10 duration-1000">

            {/* HEADER LUXURY - DOMÍNIO LAUDO */}
            <header className="bg-slate-900 rounded-[4rem] p-12 text-white border-b-[15px] border-blue-600 shadow-2xl relative">
                <div className="flex justify-between items-center relative z-10">
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 text-blue-400">
                            <FileSearch size={22} />
                            <span className="text-[10px] font-black uppercase tracking-[0.6em]">
                                Módulo de Edição Intelectual
                            </span>
                        </div>
                        <h1 className="text-5xl font-black uppercase italic tracking-tighter">
                            Câmara de Revisão
                        </h1>
                    </div>
                    <div className="text-right">
                        <span className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">
                            Responsável Técnico
                        </span>
                        <span className="text-xl font-black text-white uppercase italic">
                            Marcus Ferraz
                        </span>
                    </div>
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">

                {/* ÁREA DE ESCRITA (COL 1-3) */}
                <div className="lg:col-span-3 space-y-10">
                    <section className="bg-white rounded-[4rem] p-14 border-[12px] border-white shadow-luxury">
                        <div className="flex items-center gap-4 mb-10 border-b border-slate-100 pb-8">
                            <MessageSquareQuote className="text-blue-600" size={30} />
                            <h3 className="text-2xl font-black uppercase italic text-slate-900 tracking-tighter">
                                Parecer Definitivo
                            </h3>
                        </div>

                        <div className="space-y-8">
                            <div className="group">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 block ml-4">
                                    Conclusão Narrativa do Perito
                                </label>
                                <textarea
                                    value={narrativa.parecerFinal}
                                    onChange={(e) => setNarrativa({ ...narrativa, parecerFinal: e.target.value })}
                                    className="w-full h-[500px] bg-slate-50 border-4 border-slate-100 p-12 rounded-[3.5rem] text-sm font-bold text-slate-700 leading-relaxed focus:border-blue-600 outline-none transition-all italic shadow-inner"
                                />
                            </div>
                        </div>
                    </section>
                </div>

                {/* SIDEBAR DE AUDITORIA (COL 4) */}
                <div className="space-y-8">
                    <div className="bg-slate-900 rounded-[4rem] p-10 text-white border border-white/5 shadow-2xl">
                        <h4 className="text-[11px] font-black text-blue-400 uppercase tracking-[0.3em] mb-10 flex items-center gap-2">
                            <ShieldCheck size={18} /> Check de Normas
                        </h4>

                        <div className="space-y-10">
                            <div className="flex gap-4 p-6 bg-white/5 rounded-3xl border border-white/10">
                                <AlertTriangle className="text-amber-500 shrink-0" size={20} />
                                <p className="text-[10px] font-bold text-slate-300 leading-tight">
                                    Certifique-se de que a justificativa do fator regional condiz com a NBR 14653.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <span className="text-[9px] font-black text-slate-500 uppercase">
                                    Resumo Técnico:
                                </span>
                                <div className="p-6 bg-blue-600/10 rounded-3xl border border-blue-600/20">
                                    <p className="text-[16px] font-black text-blue-400 italic leading-none">
                                        R$ {valorFormatado}
                                    </p>
                                    <p className="text-[8px] font-bold text-slate-400 uppercase mt-2">
                                        Valor Homogeneizado
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={confirmarRevisao}
                        className="w-full py-14 bg-blue-600 text-white rounded-[4rem] font-black text-xs uppercase tracking-[0.5em] shadow-luxury hover:bg-slate-900 transition-all flex items-center justify-center gap-4"
                    >
                        CONFIRMAR TEXTO
                        <Save size={24} />
                    </button>
                </div>
            </div>

            <footer className="flex flex-col items-center gap-4 opacity-30">
                <BookOpen size={30} className="text-slate-900" />
                <span className="text-[8px] font-black uppercase tracking-[1em] text-slate-900">
                    Monte Hermom SaaS • Revisão Narrativa
                </span>
            </footer>
        </div>
    );
}